import {
  IconCheckMS,
  IconFormatAlignCenterMS,
  IconFormatAlignJustifyMS,
  IconFormatAlignLeftMS,
  IconFormatAlignRightMS,
  IconFormatBoldMS,
  IconFormatClearMS,
  IconFormatColorTextMS,
  IconFormatItalicMS,
  IconFormatListBulletedMS,
  IconFormatListNumberedMS,
  IconFormatStrikethroughMS,
  IconFormatUnderlinedMS,
  IconInkHighlighterMS,
  IconLinkMS,
} from '@/constants/icons';
import { Popover, TextField } from '@mui/material';
import { Editor } from '@tiptap/react';
import { MouseEvent, useState } from 'react';

import {
  ApplyLinkButton,
  ColorGrid,
  ColorPopoverContent,
  ColorPopoverFooter,
  ColorSwatch,
  ConfirmColorButton,
  ConfirmCustomColorButton,
  CustomColorCell,
  CustomColorInput,
  Divider,
  LinkPopoverContent,
  ToolbarButton,
  ToolbarContainer,
} from './styles';

const TEXT_COLORS = [
  '#1A1A1A',
  '#E31B0C',
  '#F79009',
  '#EAB308',
  '#12B76A',
  '#2970FF',
  '#7C3AED',
  '#475467',
];

const HIGHLIGHT_COLORS = [
  '#FEF08A',
  '#BBF7D0',
  '#BFDBFE',
  '#FBCFE8',
  '#FED7AA',
  '#E9D5FF',
  '#FECACA',
  '#E5E7EB',
];

interface ToolbarProps {
  editor: Editor | null;
}

export function Toolbar({ editor }: ToolbarProps): JSX.Element | null {
  const [linkAnchor, setLinkAnchor] = useState<HTMLElement | null>(null);
  const [linkUrl, setLinkUrl] = useState('');
  const [colorAnchor, setColorAnchor] = useState<HTMLElement | null>(null);
  const [highlightAnchor, setHighlightAnchor] = useState<HTMLElement | null>(
    null
  );

  if (!editor) return null;

  const handleOpenLink = (event: MouseEvent<HTMLElement>): void => {
    setLinkUrl((editor.getAttributes('link').href as string) ?? '');
    setLinkAnchor(event.currentTarget);
  };

  const handleApplyLink = (): void => {
    if (linkUrl.trim()) {
      editor
        .chain()
        .focus()
        .extendMarkRange('link')
        .setLink({ href: linkUrl.trim() })
        .run();
    } else {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    }
    setLinkAnchor(null);
  };

  const handleSelectColor = (color: string): void => {
    editor.chain().focus().setColor(color).run();
    setColorAnchor(null);
  };

  const handleSelectHighlight = (color: string): void => {
    editor.chain().focus().toggleHighlight({ color }).run();
    setHighlightAnchor(null);
  };

  const handleCustomColorChange = (color: string): void => {
    editor.chain().focus().setColor(color).run();
  };

  const handleCustomHighlightChange = (color: string): void => {
    editor.chain().focus().toggleHighlight({ color }).run();
  };

  const currentColor =
    (editor.getAttributes('textStyle').color as string) ?? '';
  const currentHighlight =
    (editor.getAttributes('highlight').color as string) ?? '';

  return (
    <ToolbarContainer>
      <ToolbarButton
        selected={editor.isActive('bold')}
        onClick={() => editor.chain().focus().toggleBold().run()}
      >
        {IconFormatBoldMS}
      </ToolbarButton>
      <ToolbarButton
        selected={editor.isActive('italic')}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      >
        {IconFormatItalicMS}
      </ToolbarButton>
      <ToolbarButton
        selected={editor.isActive('underline')}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
      >
        {IconFormatUnderlinedMS}
      </ToolbarButton>
      <ToolbarButton
        selected={editor.isActive('strike')}
        onClick={() => editor.chain().focus().toggleStrike().run()}
      >
        {IconFormatStrikethroughMS}
      </ToolbarButton>

      <Divider />

      <ToolbarButton
        onClick={() =>
          editor.chain().focus().unsetAllMarks().clearNodes().run()
        }
      >
        {IconFormatClearMS}
      </ToolbarButton>

      <Divider />

      <ToolbarButton onClick={(event) => setColorAnchor(event.currentTarget)}>
        {IconFormatColorTextMS}
      </ToolbarButton>
      <Popover
        open={Boolean(colorAnchor)}
        anchorEl={colorAnchor}
        onClose={() => setColorAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <ColorPopoverContent>
          <ColorGrid>
            {TEXT_COLORS.map((color) => (
              <ColorSwatch
                key={color}
                type="button"
                swatchColor={color}
                selected={currentColor.toLowerCase() === color.toLowerCase()}
                onClick={() => handleSelectColor(color)}
              />
            ))}
            <CustomColorCell>
              <CustomColorInput
                type="color"
                value={currentColor || '#000000'}
                onChange={(e) => handleCustomColorChange(e.target.value)}
              />
              <ConfirmCustomColorButton onClick={() => setColorAnchor(null)}>
                {IconCheckMS}
              </ConfirmCustomColorButton>
            </CustomColorCell>
          </ColorGrid>
          <ColorPopoverFooter>
            <ConfirmColorButton onClick={() => setColorAnchor(null)}>
              confirmar
            </ConfirmColorButton>
          </ColorPopoverFooter>
        </ColorPopoverContent>
      </Popover>

      <ToolbarButton
        selected={editor.isActive('highlight')}
        onClick={(event) => setHighlightAnchor(event.currentTarget)}
      >
        {IconInkHighlighterMS}
      </ToolbarButton>
      <Popover
        open={Boolean(highlightAnchor)}
        anchorEl={highlightAnchor}
        onClose={() => setHighlightAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <ColorPopoverContent>
          <ColorGrid>
            {HIGHLIGHT_COLORS.map((color) => (
              <ColorSwatch
                key={color}
                type="button"
                swatchColor={color}
                selected={
                  currentHighlight.toLowerCase() === color.toLowerCase()
                }
                onClick={() => handleSelectHighlight(color)}
              />
            ))}
            <CustomColorCell>
              <CustomColorInput
                type="color"
                value={currentHighlight || '#FFFFFF'}
                onChange={(e) => handleCustomHighlightChange(e.target.value)}
              />
              <ConfirmCustomColorButton
                onClick={() => setHighlightAnchor(null)}
              >
                {IconCheckMS}
              </ConfirmCustomColorButton>
            </CustomColorCell>
          </ColorGrid>
          <ColorPopoverFooter>
            <ConfirmColorButton onClick={() => setHighlightAnchor(null)}>
              confirmar
            </ConfirmColorButton>
          </ColorPopoverFooter>
        </ColorPopoverContent>
      </Popover>

      <Divider />

      <ToolbarButton
        selected={editor.isActive('orderedList')}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      >
        {IconFormatListNumberedMS}
      </ToolbarButton>
      <ToolbarButton
        selected={editor.isActive('bulletList')}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      >
        {IconFormatListBulletedMS}
      </ToolbarButton>

      <Divider />

      <ToolbarButton
        selected={editor.isActive('link')}
        onClick={handleOpenLink}
      >
        {IconLinkMS}
      </ToolbarButton>
      <Popover
        open={Boolean(linkAnchor)}
        anchorEl={linkAnchor}
        onClose={() => setLinkAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <LinkPopoverContent>
          <TextField
            size="small"
            label="URL"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleApplyLink();
            }}
          />
          <ApplyLinkButton onClick={handleApplyLink}>aplicar</ApplyLinkButton>
        </LinkPopoverContent>
      </Popover>

      <Divider />

      <ToolbarButton
        selected={editor.isActive({ textAlign: 'left' })}
        onClick={() => editor.chain().focus().setTextAlign('left').run()}
      >
        {IconFormatAlignLeftMS}
      </ToolbarButton>
      <ToolbarButton
        selected={editor.isActive({ textAlign: 'center' })}
        onClick={() => editor.chain().focus().setTextAlign('center').run()}
      >
        {IconFormatAlignCenterMS}
      </ToolbarButton>
      <ToolbarButton
        selected={editor.isActive({ textAlign: 'right' })}
        onClick={() => editor.chain().focus().setTextAlign('right').run()}
      >
        {IconFormatAlignRightMS}
      </ToolbarButton>
      <ToolbarButton
        selected={editor.isActive({ textAlign: 'justify' })}
        onClick={() => editor.chain().focus().setTextAlign('justify').run()}
      >
        {IconFormatAlignJustifyMS}
      </ToolbarButton>
    </ToolbarContainer>
  );
}
